import { Eye, RefreshCw, Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUploadJobs } from "@noa/api-client";
import { SOURCE_ICONS } from "./source-icons";
import type { IngestionJobSummary } from "@noa/api-core";
import { JobPhaseTag } from "./job-phase-tag";
import { Button, Empty, Tooltip, DataTable, type DataTableColumn } from "~/components/ui";

const PAGE_SIZE = 20;

type UploadJobHistoryProps = {
  onViewJob: (jobId: string) => void;
  hideTitle?: boolean;
  onNavigateToUpload?: () => void;
};

function formatDate(date: string | null | undefined): string {
  if (!date) return "—";
  return new Date(date).toLocaleString();
}

function truncateJobId(jobId: string): string {
  // Show first 8 chars of UUID
  return jobId.length > 8 ? `${jobId.slice(0, 8)}…` : jobId;
}

export function UploadJobHistory({ onViewJob, hideTitle = false, onNavigateToUpload }: UploadJobHistoryProps) {
  const { t } = useTranslation("admin");
  const [page, setPage] = useState(1);
  const { jobs, total, isLoading, refetch } = useUploadJobs({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const columns: DataTableColumn<IngestionJobSummary>[] = [
    {
      title: t("uploadAdmin.history.columns.id"),
      key: "id",
      width: 120,
      render: ({ id }) => (
        <Tooltip title={id}>
          <code className="text-xs">{truncateJobId(id)}</code>
        </Tooltip>
      ),
    },
    {
      title: t("uploadAdmin.history.columns.source"),
      key: "source_type",
      width: 80,
      align: "center",
      render: ({ source_type }) => (
        <Tooltip title={t(`uploadAdmin.source.${source_type}`)}>
          <span className="text-lg">{SOURCE_ICONS[source_type ?? "api"] || SOURCE_ICONS.api}</span>
        </Tooltip>
      ),
    },
    {
      title: t("uploadAdmin.history.columns.phase"),
      key: "phase",
      width: 120,
      render: (record) => (
        <JobPhaseTag
          phase={record.phase}
          sourceType={record.source_type || "blob"}
          syncProgress={record.sync_progress}
        />
      ),
    },
    {
      title: t("uploadAdmin.history.columns.files"),
      key: "files",
      width: 100,
      align: "center",
      render: (record) => (
        <span>
          {record.ingest_completed}/{record.total_files}
        </span>
      ),
    },
    {
      title: t("uploadAdmin.history.columns.created"),
      key: "created_at",
      width: 180,
      render: (record) => formatDate(record.created_at),
    },
    {
      title: t("uploadAdmin.history.columns.completed"),
      key: "completed_at",
      width: 180,
      render: (record) => formatDate(record.completed_at),
    },
    {
      key: "actions",
      width: 80,
      align: "center",
      render: (record) => (
        <Button
          variant="text"
          icon={<Eye size={14} />}
          onClick={() => onViewJob(record.id)}
          aria-label={t("uploadAdmin.history.viewDetails")}
          data-testid={`view-job-${record.id}`}
        />
      ),
    },
  ];

  if (!isLoading && total === 0) {
    return (
      <div>
        {!hideTitle && <h3 className="text-lg font-medium mb-4">{t("uploadAdmin.history.title")}</h3>}
        <Empty description={t("uploadAdmin.history.noJobs")}>
          {onNavigateToUpload && (
            <Button variant="primary" icon={<Upload size={14} />} onClick={onNavigateToUpload}>
              {t("uploadAdmin.actions.upload")}
            </Button>
          )}
        </Empty>
      </div>
    );
  }

  return (
    <div>
      {!hideTitle && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">{t("uploadAdmin.history.title")}</h3>
          <Button
            icon={<RefreshCw size={14} />}
            onClick={() => refetch()}
            loading={isLoading}
            data-testid="refresh-jobs-button"
          >
            {t("common:refresh")}
          </Button>
        </div>
      )}
      {hideTitle && (
        <div className="flex justify-end mb-4">
          <Button
            icon={<RefreshCw size={14} />}
            onClick={() => refetch()}
            loading={isLoading}
            data-testid="refresh-jobs-button"
          >
            {t("common:refresh")}
          </Button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={jobs}
        rowKey={(job) => job.id}
        loading={isLoading}
        pagination={{
          total,
          pageSize: PAGE_SIZE,
          current: page,
          onChange: setPage,
          hideOnSinglePage: true,
        }}
      />
    </div>
  );
}
